// Breach Step 6: Risk Assessment
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { Label } from '../../../../components/ui/label';
import { Slider } from '../../../../components/ui/slider';
import { Textarea } from '../../../../components/ui/textarea';
import { Gauge, PenLine, RotateCcw } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { IMPACT_AREAS, SEVERITY_LABELS, LIKELIHOOD_LABELS, getRiskBand } from '../constants';
import { ScoredAccordionGroup } from '../../../../components/workflow';

const ScoringMatrix = ({ autoSev, autoLik, overrideSev, overrideLik, onCellClick, isOverrideMode }) => {
  const getCellColor = (sev, lik) => {
    const score = sev * lik;
    if (score <= 4) return 'bg-green-500/25 text-green-800 dark:text-green-300';
    if (score <= 9) return 'bg-yellow-500/25 text-yellow-800 dark:text-yellow-300';
    if (score <= 15) return 'bg-orange-500/25 text-orange-800 dark:text-orange-300';
    return 'bg-red-500/25 text-red-800 dark:text-red-300';
  };
  const isAuto = (r, c) => r === autoSev && c === autoLik;
  const isOverride = (r, c) => overrideSev && r === overrideSev && c === overrideLik;

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[400px]">
        <div className="flex">
          <div className="w-24 shrink-0" />
          {LIKELIHOOD_LABELS.map((l, i) => (
            <div key={i} className="flex-1 text-center text-xs font-medium text-muted-foreground pb-1">{l}</div>
          ))}
        </div>
        {[...Array(5)].map((_, ri) => {
          const sev = 5 - ri;
          return (
            <div key={sev} className="flex">
              <div className="w-24 shrink-0 flex items-center text-xs font-medium text-muted-foreground pr-2 justify-end">
                {SEVERITY_LABELS[sev - 1]}
              </div>
              {[1, 2, 3, 4, 5].map(lik => {
                const score = sev * lik;
                const auto = isAuto(sev, lik);
                const over = isOverride(sev, lik);
                return (
                  <div key={lik} className="flex-1 p-0.5">
                    <button type="button" data-testid={`matrix-cell-${sev}-${lik}`}
                      onClick={() => isOverrideMode && onCellClick(sev, lik)}
                      className={cn(
                        "w-full aspect-square rounded-md flex items-center justify-center text-xs font-mono font-bold transition-all relative",
                        getCellColor(sev, lik),
                        isOverrideMode && "cursor-pointer hover:ring-2 hover:ring-primary",
                        !isOverrideMode && "cursor-default",
                        over && "ring-2 ring-primary ring-offset-1",
                        auto && !over && overrideSev && "border-2 border-dashed border-foreground/40",
                        auto && !overrideSev && "ring-2 ring-foreground ring-offset-1"
                      )}>
                      {score}
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })}
        <div className="flex mt-1">
          <div className="w-24 shrink-0" />
          <div className="flex-1 text-center text-xs text-muted-foreground pt-1">Likelihood &rarr;</div>
        </div>
      </div>
    </div>
  );
};

export default function Step6RiskAssessment({
  formData, updateField, riskResult, overrideMode, setOverrideMode,
  handleOverrideCell, resetOverride, RISK_VARIABLES
}) {
  const result = riskResult;
  const autoSev = formData.auto_severity;
  const autoLik = formData.auto_likelihood;
  const autoScore = autoSev && autoLik ? autoSev * autoLik : null;
  const overSev = formData.override_severity;
  const overLik = formData.override_likelihood;
  const overScore = overSev && overLik ? overSev * overLik : null;
  const finalScore = overScore || autoScore;
  const finalBand = finalScore ? getRiskBand(finalScore) : null;
  const delta = overScore && autoScore ? overScore - autoScore : null;

  return (
    <div className="space-y-6">
      <div><h2 className="text-2xl font-bold">Risk Assessment</h2>
        <p className="text-muted-foreground mt-1">Points from Steps 4 & 5 auto-feed the fuzzy engine. Add impact areas below, then review the 5x5 matrix.</p>
      </div>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Impact Areas</CardTitle>
          <CardDescription>Potential harm to data subjects' rights and freedoms (GDPR Recitals 75/85)</CardDescription></CardHeader>
        <CardContent>
          <ScoredAccordionGroup categories={IMPACT_AREAS} selected={formData.impact_areas} onChange={v => updateField('impact_areas', v)} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Fuzzy Logic Inputs</CardTitle>
          <CardDescription>Auto-populated from your selections. Adjust manually if needed.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {RISK_VARIABLES.map((variable, idx) => {
            const factor = result?.factors?.find(f => f.variableId === variable.id);
            return (
              <div key={variable.id} className="space-y-2 p-3 border rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Label className="font-medium text-sm">{variable.name}</Label>
                    {variable.inverse && <Badge variant="outline" className="text-xs">Inverse</Badge>}
                  </div>
                  <span className="text-sm font-mono font-medium">{formData.risk_input_values[idx] || 0}</span>
                </div>
                <Slider data-testid={`risk-slider-${variable.id}`}
                  value={[formData.risk_input_values[idx] || 0]}
                  onValueChange={([v]) => {
                    const u = [...formData.risk_input_values]; u[idx] = v;
                    updateField('risk_input_values', u);
                  }}
                  min={0} max={100} step={1} />
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  {variable.ticks.map((tick, i) => <span key={i} className="text-center" style={{ width: '20%' }}>{tick}</span>)}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Severity x Likelihood Matrix</CardTitle>
              <div className="flex gap-2">
                {overSev && (
                  <Button variant="ghost" size="sm" onClick={resetOverride} className="text-xs h-7">
                    <RotateCcw className="h-3 w-3 mr-1" />Reset Override
                  </Button>
                )}
                <Button variant={overrideMode ? "default" : "outline"} size="sm" onClick={() => setOverrideMode(!overrideMode)} className="text-xs h-7">
                  <PenLine className="h-3 w-3 mr-1" />{overrideMode ? 'Done' : 'DPO Override'}
                </Button>
              </div>
            </div>
            <CardDescription>
              {overrideMode ? 'Click a cell to override the auto-plotted position' :
                autoSev ? `Auto-plotted: Severity ${SEVERITY_LABELS[autoSev - 1]} (${autoSev}) x Likelihood ${LIKELIHOOD_LABELS[autoLik - 1]} (${autoLik})` :
                'Adjust inputs above to see the matrix position'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScoringMatrix autoSev={autoSev} autoLik={autoLik} overrideSev={overSev} overrideLik={overLik}
              onCellClick={handleOverrideCell} isOverrideMode={overrideMode} />
            <div className="flex flex-wrap gap-3 mt-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border-2 border-foreground/60" /> Auto-plotted</span>
              {overSev && <span className="flex items-center gap-1"><span className="w-3 h-3 rounded ring-2 ring-primary" /> Override</span>}
              {overSev && autoSev && <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border-2 border-dashed border-foreground/40" /> Original</span>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Gauge className="h-5 w-5" />Risk Result</CardTitle></CardHeader>
          <CardContent>
            {finalBand ? (
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-5xl font-bold font-mono" data-testid="matrix-score" style={{ color: finalBand.color }}>
                    {finalScore}
                  </div>
                  <Badge className="mt-2" style={{ backgroundColor: finalBand.color, color: '#fff' }}>{finalBand.level}</Badge>
                  {delta !== null && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      Override delta: <span className="font-mono">{delta > 0 ? '+' : ''}{delta}</span> ({autoScore} &rarr; {overScore})
                    </div>
                  )}
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Recommended Action ({finalBand.ref}):</p>
                  <p className="text-sm font-medium">{finalBand.action}</p>
                </div>
                {result && (
                  <div className="text-xs text-muted-foreground">
                    Composite fuzzy score: <span className="font-mono font-medium">{result.score}/100</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm">Complete inputs to see risk assessment</div>
            )}
          </CardContent>
        </Card>
      </div>

      {overSev && (
        <Card className="border-primary/30">
          <CardHeader className="pb-3"><CardTitle className="text-base">DPO Override Justification <span className="text-destructive">*</span></CardTitle>
            <CardDescription>A written justification is mandatory when overriding the auto-plotted position.</CardDescription></CardHeader>
          <CardContent>
            <Textarea data-testid="override-justification" value={formData.override_justification || ''}
              onChange={e => updateField('override_justification', e.target.value)}
              placeholder="Explain why you are overriding the automated risk assessment..." rows={4} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
